import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import 'bootstrap/dist/css/bootstrap.min.css';
import { createBrowserRouter, RouterProvider } from "react-router-dom";

// Core components loaded immediately (needed for layout)
import Layout from "./Components/Layout.js";
import LoadingSpinner from "./Components/LoadingSpinner.js";
import ErrorBoundary from "./Components/ErrorBoundary.js";
import NotFound from "./Components/NotFound.js";
import App from './App'; // Import App directly - don't lazy load the home page

// Lazy load all route components for code splitting
const Home = lazy(() => import('./Components/Home.js'));
const About = lazy(() => import('./Components/About.js'));
const Portfolio = lazy(() => import('./Components/Portfolio'));
const Blog = lazy(() => import('./Components/Blog.js'));
const Contact = lazy(() => import('./Components/Contact.js'));
const StatsPage = lazy(() => import('./Components/StatsPage.js'));
const GraphicDesign = lazy(() => import('./Components/GraphicDesign.js'));
const WebDevelopment = lazy(() => import('./Components/WebDevelopment.js'));
const ServicesMain = lazy(() => import('./Components/ServicesMain.js'));
const ProposalBuilderSingleScreen = lazy(() => import('./Components/ProposalBuilderProfessional.js'));
const ProposalReview = lazy(() => import('./Components/ProposalReview.js'));
const ProposalNext = lazy(() => import('./Components/ProposalNext.js'));

// Lazy load portfolio pages
const Amperes = lazy(() => import('./Components/Portfolio/Design/Amperes.js'));
const BG = lazy(() => import('./Components/Portfolio/Design/BG.js'));
const BossStrategies = lazy(() => import('./Components/Portfolio/Design/BossStrategies.js'));
const BrownForman = lazy(() => import('./Components/Portfolio/Design/BrownForman.js'));
const DreamPosters = lazy(() => import('./Components/Portfolio/Design/DreamPosters.js'));
const EastCoast = lazy(() => import('./Components/Portfolio/Design/EastCoast.js'));
const Mindset = lazy(() => import('./Components/Portfolio/Design/Mindset.js'));
const Phantom = lazy(() => import('./Components/Portfolio/Design/Phantom.js'));
const QRT = lazy(() => import('./Components/Portfolio/Design/QRT.js'));
const RangerHB = lazy(() => import('./Components/Portfolio/Design/RangerHB.js'));
const SeaKayakGA = lazy(() => import('./Components/Portfolio/Design/SeaKayakGA.js'));
const SpaceMonkey = lazy(() => import('./Components/Portfolio/Design/SpaceMonkey.js'));
const StokedTwo = lazy(() => import('./Components/Portfolio/Design/StokedTwo.js'));
const StokedThree = lazy(() => import('./Components/Portfolio/Design/StokedThree.js'));
const Clover = lazy(() => import('./Components/Portfolio/Design/Clover.js'));
const PortfolioUpgrade = lazy(() => import('./Components/Portfolio/Design/PortfolioUpgrade.js'));
const IdealExperience = lazy(() => import('./Components/Portfolio/Design/IdealExperience.js'));
const MD99 = lazy(() => import('./Components/Portfolio/Design/MD99.js'));
const AgCon = lazy(() => import('./Components/Portfolio/Design/AgCon.js'));
const SilentBattle = lazy(() => import('./Components/Portfolio/Design/SilentBattle.js'));
const RowansCreek = lazy(() => import('./Components/Portfolio/Design/RowansCreek.js'));
const Railbird = lazy(() => import('./Components/Portfolio/Design/Railbird.js'));
const ThreeWillows = lazy(() => import('./Components/Portfolio/Design/ThreeWillows.js'));
const TotalPtConnect = lazy(() => import('./Components/Portfolio/Design/TotalPtConnect.js'));
const StokedOne = lazy(() => import('./Components/Portfolio/Design/StokedOne.js'));
// Admin components removed for security

// Helper to wrap components with Suspense
const withSuspense = (Component, useLayout = true) => {
  const wrapped = (
    <Suspense fallback={<LoadingSpinner />}>
      <Component />
    </Suspense>
  );
  return useLayout ? <Layout>{wrapped}</Layout> : wrapped;
};

// Helper for components that need props
const withSuspenseAndProps = (Component, props, useLayout = true) => {
  const wrapped = (
    <Suspense fallback={<LoadingSpinner />}>
      <Component {...props} />
    </Suspense>
  );
  return useLayout ? <Layout>{wrapped}</Layout> : wrapped;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
  },
  {
    path: "/home",
    element: withSuspense(Home),
  },
  {
    path: "/about",
    element: withSuspense(About),
  },
  {
    path: "/portfolio",
    element: withSuspense(Portfolio),
  },
  {
    path: "/blog",
    element: withSuspense(Blog),
  },
  {
    path: "/contact",
    element: withSuspense(Contact),
  },
  {
    path: "/stats",
    element: withSuspense(StatsPage, false),
  },
  {
    path: "/graphic-design",
    element: withSuspense(GraphicDesign),
  },
  {
    path: "/web-development",
    element: withSuspense(WebDevelopment),
  },
  {
    path: "/services",
    element: withSuspense(ServicesMain),
  },
  {
    path: "/proposal-builder",
    element: withSuspense(ProposalBuilderSingleScreen, false),
  },
  {
    path: "/proposal-review",
    element: withSuspense(ProposalReview),
  },
  {
    path: "/proposal-next",
    element: withSuspense(ProposalNext),
  },
  {
    path: "/amperes",
    element: withSuspense(Amperes),
  },
  {
    path: "/bg",
    element: withSuspense(BG),
  },
  {
    path: "/boss-strategies",
    element: withSuspense(BossStrategies),
  },
  {
    path: "/brown-forman",
    element: withSuspense(BrownForman),
  },
  {
    path: "/dream-posters",
    element: withSuspense(DreamPosters),
  },
  {
    path: "/east-coast",
    element: withSuspense(EastCoast),
  },
  {
    path: "/mindset",
    element: withSuspense(Mindset),
  },
  {
    path: "/phantom",
    element: withSuspense(Phantom),
  },
  {
    path: "/qrt",
    element: withSuspense(QRT),
  },
  {
    path: "/ranger",
    element: withSuspense(RangerHB),
  },
  {
    path: "/skg",
    element: withSuspense(SeaKayakGA),
  },
  {
    path: "/space-monkey",
    element: withSuspense(SpaceMonkey),
  },
  {
    path: "/stoked-two",
    element: withSuspense(StokedTwo),
  },
  {
    path: "/stoked-three",
    element: withSuspense(StokedThree),
  },
  {
    path: "/clover",
    element: withSuspense(Clover),
  },
  {
    path: "/portfolio-upgrade",
    element: withSuspense(PortfolioUpgrade),
  },
  {
    path: "/md99",
    element: withSuspense(MD99),
  },
  {
    path: "/ideal-experience",
    element: withSuspense(IdealExperience),
  },
  {
    path: "/ag-con",
    element: withSuspense(AgCon),
  },
  {
    path: "/silent-battle",
    element: withSuspense(SilentBattle),
  },
  {
    path: "/rowans-creek",
    element: withSuspense(RowansCreek),
  },
  {
    path: "/railbird",
    element: withSuspense(Railbird),
  },
  {
    path: "/three-willows",
    element: withSuspense(ThreeWillows),
  },
  {
    path: "/total-pt-connect",
    element: withSuspense(TotalPtConnect),
  },
  {
    path: "/stoked-one",
    element: withSuspense(StokedOne),
  },
  // Admin routes removed for security
  {
    path: "*",
    element: <Layout><NotFound /></Layout>,
  },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </React.StrictMode>
);
